// Left side-nav view switcher. The app is a single page whose top-level sections (Skill-Based
// Diagnostic + the roadmap placeholders) are `.view` blocks; clicking a `.nav-item` shows its view
// and gold-highlights the item. Pure show/hide (no routing) so the stateful scorer view is never
// torn down or re-initialised — its DOM stays mounted, just visually toggled.

import { setTagline } from './tagline.js';

// What's New "unseen" indicator. Bump WHATS_NEW_VERSION whenever a new release entry is added to the
// What's New page (index.html) — every browser then shows a gold dot on the nav item until the page is
// opened, at which point the current version is stored and the dot clears. Per-browser, best-effort.
const WHATS_NEW_VERSION = '2026-09';
const WHATS_NEW_KEY = 'score.whatsnew.seen';

function whatsNewDots(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>('.nav-item[data-view="whats-new"] .nav-dot'));
}

/** Show the gold dot if this browser hasn't opened the current What's New version yet. */
function refreshWhatsNewDot(): void {
  let seen = '';
  try {
    seen = localStorage.getItem(WHATS_NEW_KEY) || '';
  } catch {
    /* storage unavailable — leave the dot hidden */
  }
  const unseen = seen !== WHATS_NEW_VERSION;
  for (const dot of whatsNewDots()) dot.classList.toggle('is-shown', unseen);
}

/** Record that the current What's New version has been opened, and clear the dot. */
function markWhatsNewSeen(): void {
  try {
    localStorage.setItem(WHATS_NEW_KEY, WHATS_NEW_VERSION);
  } catch {
    /* ignore */
  }
  for (const dot of whatsNewDots()) dot.classList.remove('is-shown');
}

// Set once wireSideNav runs, so other modules (e.g. the Home path-picker cards) can switch views
// programmatically via navigateTo() without each re-implementing the show/hide + title logic.
let showView: ((view: string) => void) | null = null;

/** Switch to a top-level view by its data-view name. No-op until wireSideNav() has run. */
export function navigateTo(view: string): void {
  showView?.(view);
}

// View-change subscribers — a stateful page (e.g. Buyer-Side) uses this to refresh when it's shown,
// since views are shown/hidden, never re-mounted.
const viewListeners: Array<(view: string) => void> = [];
export function onViewChange(cb: (view: string) => void): void {
  viewListeners.push(cb);
}

// Role-gated nav (Identity roadmap Phase 1). Each entry is a nav view that requires a Permission; a
// view NOT listed is available to every role (Home, the three lenses, Anonymize, Getting Started,
// Support, Trust). This is UX only — hiding a menu item; the worker enforces the real gate per
// endpoint. Permission strings mirror apps/worker/src/auth/roles.ts.
export const NAV_PERMISSION: Readonly<Record<string, string>> = {
  trends: 'team_history', // Trends (under The CRO Zone group)
  heatmap: 'team_history', // Heatmap (under The CRO Zone group; shares the Trends gate)
  'bespoke-playbook': 'cohort', // Cohort Diagnostic (under The CRO Zone group)
  'team-trends': 'team_history', // Team Trends (under The CRO Zone group)
  teams: 'manage_users', // Teams management (under The CRO Zone group; managers only)
  history: 'team_history', // Report History (under The CRO Zone group)
  compare: 'team_history', // Compare (under The CRO Zone group)
  scorecards: 'scorecards',
  account: 'settings', // Settings
  integration: 'integration',
  billing: 'billing',
};

/** Pure: which nav views to hide given the caller's permissions. Unit-tested. */
export function hiddenNavViews(permissions: readonly string[]): string[] {
  const have = new Set(permissions);
  return Object.entries(NAV_PERMISSION)
    .filter(([, perm]) => !have.has(perm))
    .map(([view]) => view);
}

/** Hide the nav items a role cannot use (UX only). Best-effort: on any failure the nav is left intact
 *  (fail-open for the menu; the worker is the authority). A collapsible group whose items are ALL
 *  hidden (e.g. a member has none of The CRO Zone pages) is hidden too, so no empty group header shows.
 *
 *  The CRO Zone group is now visible to every account owner (super_user has the cohort / team_history /
 *  manage_users permissions); it shows by default in the markup and is only hidden here for a role that
 *  lacks all of its items (e.g. a plain member). A free-trial owner still SEES it — the paid-plan gate on
 *  the CRO pages themselves (main.ts) is what asks them to upgrade before use. */
export function applyRoleNav(permissions: readonly string[]): void {
  for (const view of hiddenNavViews(permissions)) {
    document.querySelectorAll<HTMLElement>(`.nav-item[data-view="${view}"]`).forEach((el) => {
      el.hidden = true;
    });
  }
  Array.from(document.querySelectorAll<HTMLElement>('.nav-group')).forEach((group) => {
    const items = Array.from(group.querySelectorAll<HTMLElement>('.nav-item'));
    if (items.length && items.every((it) => it.hidden)) group.hidden = true;
  });
}

export function wireSideNav(): void {
  const items = Array.from(document.querySelectorAll<HTMLElement>('.nav-item'));
  const views = Array.from(document.querySelectorAll<HTMLElement>('.view'));
  if (!items.length || !views.length) return;
  const pageTitle = document.getElementById('page-title');

  const show = (view: string, animate = true): void => {
    let label = '';
    for (const it of items) {
      const on = it.dataset.view === view;
      it.classList.toggle('is-active', on);
      it.setAttribute('aria-current', on ? 'page' : 'false');
      // The workspace page header mirrors the active nav item. A `data-page-title` overrides the
      // (possibly abbreviated) sidebar label — e.g. "Anonymize" in the rail, "Anonymize Transcript"
      // in the header.
      if (on) label = it.dataset.pageTitle || it.querySelector('span')?.textContent?.trim() || it.title;
    }
    for (const v of views) v.classList.toggle('is-active', v.dataset.view === view);
    if (pageTitle && label) pageTitle.textContent = label;
    // When a collapsible group is collapsed, its header carries the active state if the current page is
    // one of its children — so "you are here" is never lost. (No-op when the group is expanded.)
    Array.from(document.querySelectorAll<HTMLElement>('.nav-group')).forEach((group) => {
      group.classList.toggle('has-active-child', Boolean(group.querySelector('.nav-item.is-active')));
    });
    if (view === 'whats-new') markWhatsNewSeen();
    setTagline(view, { animate });
    for (const cb of viewListeners) cb(view);
  };

  for (const it of items) {
    it.addEventListener('click', () => {
      const v = it.dataset.view;
      if (v) show(v);
    });
  }

  // Expose programmatic navigation and sync the page title to whichever view is active on load.
  showView = show;
  const active = items.find((it) => it.classList.contains('is-active'))?.dataset.view;
  if (active) show(active, false);

  wireCollapse();
  wireGroupCollapse();
  refreshWhatsNewDot();
}

// Collapsible nav groups (One-Off Diagnostic, The CRO Zone): the chevron header folds the group's items.
// State persists per browser, per group. Keyboard-operable (real <button>); aria-expanded reflects state.
function wireGroupCollapse(): void {
  for (const group of Array.from(document.querySelectorAll<HTMLElement>('.nav-group'))) {
    const toggle = group.querySelector<HTMLElement>('.nav-group-toggle');
    if (!toggle || !group.id) continue;
    const key = `score.nav.${group.id}.collapsed`;

    const apply = (collapsed: boolean): void => {
      group.classList.toggle('is-collapsed', collapsed);
      toggle.setAttribute('aria-expanded', String(!collapsed));
    };

    let collapsed = false;
    try {
      collapsed = localStorage.getItem(key) === '1';
    } catch {
      /* storage unavailable — default expanded */
    }
    apply(collapsed);

    toggle.addEventListener('click', () => {
      collapsed = !group.classList.contains('is-collapsed');
      apply(collapsed);
      try {
        localStorage.setItem(key, collapsed ? '1' : '0');
      } catch {
        /* ignore */
      }
    });
  }
}

// Collapse toggle: shrink the rail to an icon-only strip (labels hidden). State persists per browser.
const COLLAPSE_KEY = 'score.nav.collapsed';
function wireCollapse(): void {
  const sidenav = document.getElementById('sidenav');
  const toggle = document.getElementById('sidenav-toggle');
  if (!sidenav || !toggle) return;

  const apply = (collapsed: boolean): void => {
    sidenav.classList.toggle('is-collapsed', collapsed);
    toggle.setAttribute('aria-label', collapsed ? 'Expand menu' : 'Collapse menu');
    toggle.setAttribute('aria-expanded', String(!collapsed));
  };

  let collapsed = false;
  try {
    collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    /* storage unavailable — default expanded */
  }
  apply(collapsed);

  toggle.addEventListener('click', () => {
    collapsed = !sidenav.classList.contains('is-collapsed');
    apply(collapsed);
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  });
}
